// components/Admin/Order-Management/OrderPackingSlip.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Printer,
  Download,
  X,
  Package,
  MapPin,
  User,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Loader,
  AlertCircle,
  QrCode,
  Shield
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import TantikaLogo from '../../../Assets/TantikaLogo.png';

const primaryBtn = "inline-flex items-center gap-2 bg-gradient-to-r from-clay-500 to-saffron-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0";
const secondaryBtn = "inline-flex items-center gap-2 border-2 border-cream-300 text-cocoa-700 px-4 py-2 rounded-full font-semibold text-sm hover:bg-cream-50 transition-all duration-300";
const sectionTitle = "text-sm font-display font-bold text-cocoa-800 flex items-center gap-2";
const infoBox = "bg-cream-50 border border-cream-100 p-3 rounded-xl space-y-1 text-sm";

// ========== ADMIN VERSION - Full access to all features ==========
export const AdminOrderPackingSlip = ({ order, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (order) {
      generateQRCode();
    }
  }, [order]);

  if (!order) return null;

  // ========== Helper functions with proper data extraction ==========
  const getOrderNumber = () => order.orderNumber || 'N/A';

  const getCustomerName = () => {
    if (order.customer?.name) return order.customer.name;
    if (order.customerDetails?.name) return order.customerDetails.name;
    return order.customerName || 'N/A';
  };

  const getCustomerEmail = () => {
    if (order.customer?.email) return order.customer.email;
    if (order.customerDetails?.email) return order.customerDetails.email;
    return order.customerEmail || 'N/A';
  };

  const getCustomerPhone = () => {
    if (order.customer?.phone) return order.customer.phone;
    if (order.customerDetails?.phone) return order.customerDetails.phone;
    return order.customerPhone || 'N/A';
  };

  const getCustomerAddress = () => {
    if (order.customer?.shippingAddress) {
      const address = order.customer.shippingAddress;
      if (typeof address === 'string') return address;
      const parts = [
        address.street || '',
        address.city || '',
        address.state || '',
        address.postalCode || '',
        address.country || 'India'
      ].filter(Boolean);
      return parts.join(', ') || 'No address provided';
    }

    if (order.customerDetails?.shippingAddress) {
      const address = order.customerDetails.shippingAddress;
      if (typeof address === 'string') return address;
      const parts = [
        address.street || '',
        address.city || '',
        address.state || '',
        address.postalCode || '',
        address.country || 'India'
      ].filter(Boolean);
      return parts.join(', ') || 'No address provided';
    }

    if (order.customerAddress) {
      if (typeof order.customerAddress === 'string') return order.customerAddress;
      const parts = [
        order.customerAddress.street || '',
        order.customerAddress.city || '',
        order.customerAddress.state || '',
        order.customerAddress.postalCode || '',
        order.customerAddress.country || 'India'
      ].filter(Boolean);
      return parts.join(', ') || 'No address provided';
    }

    return 'No address provided';
  };

  const getArtisanName = () => {
    if (order.items && order.items.length > 0) {
      const firstItem = order.items[0];
      return firstItem.artisanName ||
             firstItem.artisan?.businessName ||
             firstItem.artisan?.name ||
             'Unknown';
    }
    return order.artisanName || order.artisan || 'Unknown';
  };

  const getPaymentMethod = () => {
    return order.payment?.method || order.paymentMethod || 'N/A';
  };

  const getSubtotal = () => {
    return order.subtotal || 0;
  };

  const getTax = () => {
    return order.tax || 0;
  };

  const getShippingCost = () => {
    if (order.shipping?.shippingCost !== undefined) return order.shipping.shippingCost;
    if (order.shippingCost !== undefined) return order.shippingCost;
    return 0;
  };

  const getTotal = () => {
    if (order.total !== undefined && order.total !== null) return order.total;
    const subtotal = getSubtotal();
    const tax = getTax();
    const shipping = getShippingCost();
    return subtotal + tax + shipping;
  };

  const getOrderItems = () => {
    return order.items || [];
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const generateQRCode = async () => {
    setLoading(true);
    setQrError(false);

    try {
      const qrText = `https://tantikacrafts.netlify.app`;
      const qrApiUrl = `https://quickchart.io/qr?text=${encodeURIComponent(qrText)}&size=200&margin=4&dark=1a1a2e&light=ffffff&format=png`;

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = qrApiUrl;
      });

      setQrCodeUrl(qrApiUrl);
    } catch (error) {
      console.error('QR Code generation error:', error);
      setQrError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!printRef.current) return;

    try {
      setGeneratingPDF(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a5');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`packing-slip-${order.orderNumber}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const subtotal = getSubtotal();
  const tax = getTax();
  const shippingCost = getShippingCost();
  const total = getTotal();
  const items = getOrderItems();

  return (
    <div className="fixed inset-0 bg-cocoa-950/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 z-50">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp">
        <div className="sticky top-0 z-20 bg-gradient-to-br from-cream-50 via-white to-saffron-50 border-b border-cream-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-clay-500 to-saffron-500 text-white shadow-warm">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-display font-bold text-cocoa-900">
                Packing Slip
              </h3>
              <p className="text-sm text-cocoa-500">Order #{getOrderNumber()}</p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 px-3 py-1 bg-clay-100 text-clay-700 rounded-full text-xs font-semibold">
              <Shield className="w-3.5 h-3.5" />
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className={secondaryBtn}
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
              className={primaryBtn}
            >
              {generatingPDF ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-cocoa-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-gradient-to-br from-cream-100 via-cream-50 to-saffron-50 scrollbar-thin">
          <div
            ref={printRef}
            id="print-area"
            className="bg-white rounded-2xl shadow-warm p-8 max-w-2xl mx-auto print:shadow-none print:p-6"
            style={{ width: '210mm', minHeight: '148mm' }}
          >
            <div className="space-y-6">
              {/* Header with Logo */}
              <div className="flex justify-between items-start border-b-2 border-clay-200 pb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={TantikaLogo}
                    alt="তন্তিকা"
                    className="h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h1 className="text-2xl font-display font-bold bg-gradient-to-r from-clay-600 to-saffron-600 bg-clip-text text-transparent">তন্তিকা</h1>
                    <p className="text-sm text-cocoa-500">Handcrafted with ❤️</p>
                    <p className="text-sm text-cocoa-500">https://tantikacrafts.netlify.app</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-display font-bold tracking-widest text-cocoa-800">PACKING SLIP</div>
                  <div className="text-xs text-cocoa-400 mt-1">Date: {formatDate(new Date())}</div>
                  <div className="text-xs text-cocoa-400">Order: #{getOrderNumber()}</div>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <User className="w-4 h-4 text-clay-600" />
                    Customer Details
                  </h4>
                  <div className={infoBox}>
                    <p className="font-semibold text-cocoa-900">{getCustomerName()}</p>
                    <p className="text-cocoa-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {getCustomerPhone()}
                    </p>
                    <p className="text-cocoa-600 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {getCustomerEmail()}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <MapPin className="w-4 h-4 text-clay-600" />
                    Shipping Address
                  </h4>
                  <div className={infoBox}>
                    <p className="font-semibold text-cocoa-900">{getCustomerName()}</p>
                    <p className="text-cocoa-600 mt-1">{getCustomerAddress()}</p>
                    <p className="text-cocoa-600 mt-1">{getCustomerPhone()}</p>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <Calendar className="w-4 h-4 text-clay-600" />
                    Order Information
                  </h4>
                  <div className={infoBox}>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Order Date:</span> {formatDate(order.createdAt)}</p>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Artisan:</span> {getArtisanName()}</p>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Payment:</span> {getPaymentMethod()}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <CreditCard className="w-4 h-4 text-clay-600" />
                    Payment Summary
                  </h4>
                  <div className={infoBox}>
                    <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Subtotal:</span> {formatCurrency(subtotal)}</p>
                    <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Tax:</span> {formatCurrency(tax)}</p>
                    <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Shipping:</span> {formatCurrency(shippingCost)}</p>
                    <p className="font-bold text-clay-700 border-t border-clay-200 pt-1 flex justify-between">
                      <span>Total</span>
                      <span>{formatCurrency(total)}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="space-y-2">
                <h4 className={sectionTitle}>
                  <Package className="w-4 h-4 text-clay-600" />
                  Items
                </h4>
                <div className="border border-cream-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-cream-50">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">#</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">Item</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-cocoa-600">Qty</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Price</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index} className="border-t border-cream-100">
                          <td className="px-3 py-2.5 text-cocoa-400">{index + 1}</td>
                          <td className="px-3 py-2.5">
                            <div>
                              <div className="font-medium text-cocoa-900">{item.name}</div>
                              {item.sku && <div className="text-xs text-cocoa-400">SKU: {item.sku}</div>}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center text-cocoa-800">{item.quantity}</td>
                          <td className="px-3 py-2.5 text-right text-cocoa-800">{formatCurrency(item.price)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-cocoa-900">
                            {formatCurrency((item.price || 0) * (item.quantity || 1))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-cream-50 border-t border-cream-200">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right font-semibold text-cocoa-700">Subtotal</td>
                        <td className="px-3 py-2 text-right font-semibold text-cocoa-900">{formatCurrency(subtotal)}</td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Shipping</td>
                        <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(shippingCost)}</td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Tax</td>
                        <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(tax)}</td>
                      </tr>
                      <tr className="border-t-2 border-clay-400">
                        <td colSpan="4" className="px-3 py-2.5 text-right font-bold text-clay-700">Total</td>
                        <td className="px-3 py-2.5 text-right font-bold text-clay-700">{formatCurrency(total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-between items-center border-t-2 border-cream-200 pt-4">
                <div className="text-xs text-cocoa-400">
                  <p>Thank you for shopping with তন্তিকা!</p>
                  <p className="mt-1">Scan the QR code to visit our website</p>
                </div>
                <div className="flex items-center gap-4">
                  {loading ? (
                    <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center">
                      <Loader className="w-6 h-6 animate-spin text-clay-600" />
                    </div>
                  ) : qrError ? (
                    <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center flex-col">
                      <AlertCircle className="w-6 h-6 text-red-500" />
                      <span className="text-xs text-red-500">QR Error</span>
                    </div>
                  ) : qrCodeUrl ? (
                    <img
                      src={qrCodeUrl}
                      alt="Visit তন্তিকা Website"
                      className="w-20 h-20 border-2 border-cream-200 rounded-xl"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-cocoa-300" />
                    </div>
                  )}
                  <div className="text-xs text-cocoa-500">
                    <p className="font-semibold text-cocoa-800">Scan to Visit Us!</p>
                    <p className="text-cocoa-400">তন্তিকা Crafts</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-cocoa-400 border-t border-cream-200 pt-4">
                <p>Generated on {formatDate(new Date())}</p>
                <p className="mt-1">This is a system generated packing slip for order #{getOrderNumber()}</p>
                <p className="mt-1">Thank you for supporting handcrafted products! 🙏</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== ARTISAN VERSION - Limited to their own products ==========
export const ArtisanOrderPackingSlip = ({ order, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (order) {
      generateQRCode();
    }
  }, [order]);

  if (!order) return null;

  // ========== Artisan-specific helper functions ==========
  const getOrderNumber = () => order.orderNumber || 'N/A';

  const getCustomerName = () => {
    if (order.customer?.name) return order.customer.name;
    return order.customerName || 'N/A';
  };

  const getCustomerEmail = () => {
    if (order.customer?.email) return order.customer.email;
    return order.customerEmail || 'N/A';
  };

  const getCustomerPhone = () => {
    if (order.customer?.phone) return order.customer.phone;
    return order.customerPhone || 'N/A';
  };

  const getCustomerAddress = () => {
    if (order.customer?.shippingAddress) {
      const address = order.customer.shippingAddress;
      if (typeof address === 'string') return address;
      const parts = [
        address.street || '',
        address.city || '',
        address.state || '',
        address.postalCode || '',
        address.country || 'India'
      ].filter(Boolean);
      return parts.join(', ') || 'No address provided';
    }
    if (order.customerAddress) {
      if (typeof order.customerAddress === 'string') return order.customerAddress;
      const parts = [
        order.customerAddress.street || '',
        order.customerAddress.city || '',
        order.customerAddress.state || '',
        order.customerAddress.postalCode || '',
        order.customerAddress.country || 'India'
      ].filter(Boolean);
      return parts.join(', ') || 'No address provided';
    }
    return 'No address provided';
  };

  const getArtisanName = () => {
    // Get current artisan name from localStorage
    try {
      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');
      return userData.businessName || userData.fullName || userData.name || 'My Shop';
    } catch {
      return 'My Shop';
    }
  };

  const getPaymentMethod = () => {
    return order.payment?.method || order.paymentMethod || 'N/A';
  };

  const getSubtotal = () => {
    // For artisans, only show subtotal of their items
    const items = getOrderItems();
    return items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
  };

  const getTax = () => {
    return order.tax || 0;
  };

  const getShippingCost = () => {
    if (order.shipping?.shippingCost !== undefined) return order.shipping.shippingCost;
    if (order.shippingCost !== undefined) return order.shippingCost;
    return 0;
  };

  const getTotal = () => {
    if (order.total !== undefined && order.total !== null) return order.total;
    const subtotal = getSubtotal();
    const tax = getTax();
    const shipping = getShippingCost();
    return subtotal + tax + shipping;
  };

  const getOrderItems = () => {
    // Filter items for this artisan
    try {
      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');
      const artisanId = userData.artisanId || userData._id;

      if (artisanId && order.items) {
        return order.items.filter(item =>
          item.artisan === artisanId ||
          item.artisan?._id === artisanId ||
          item.artisanId === artisanId
        );
      }
    } catch (error) {
      console.error('Error filtering artisan items:', error);
    }
    return order.items || [];
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const generateQRCode = async () => {
    setLoading(true);
    setQrError(false);

    try {
      const qrText = `https://tantikacrafts.netlify.app`;
      const qrApiUrl = `https://quickchart.io/qr?text=${encodeURIComponent(qrText)}&size=200&margin=4&dark=1a1a2e&light=ffffff&format=png`;

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = qrApiUrl;
      });

      setQrCodeUrl(qrApiUrl);
    } catch (error) {
      console.error('QR Code generation error:', error);
      setQrError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!printRef.current) return;

    try {
      setGeneratingPDF(true);
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a5');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`packing-slip-${order.orderNumber}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const subtotal = getSubtotal();
  const tax = getTax();
  const shippingCost = getShippingCost();
  const total = getTotal();
  const items = getOrderItems();

  return (
    <div className="fixed inset-0 bg-cocoa-950/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 z-50">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp">
        <div className="sticky top-0 z-20 bg-gradient-to-br from-cream-50 via-white to-saffron-50 border-b border-cream-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-clay-500 to-saffron-500 text-white shadow-warm">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-display font-bold text-cocoa-900">
                Packing Slip
              </h3>
              <p className="text-sm text-cocoa-500">Order #{getOrderNumber()}</p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
              <Shield className="w-3.5 h-3.5" />
              Artisan
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className={secondaryBtn}
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
              className={primaryBtn}
            >
              {generatingPDF ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-cocoa-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-gradient-to-br from-cream-100 via-cream-50 to-saffron-50 scrollbar-thin">
          <div
            ref={printRef}
            id="print-area"
            className="bg-white rounded-2xl shadow-warm p-8 max-w-2xl mx-auto print:shadow-none print:p-6"
            style={{ width: '210mm', minHeight: '148mm' }}
          >
            <div className="space-y-6">
              {/* Header with Logo */}
              <div className="flex justify-between items-start border-b-2 border-clay-200 pb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={TantikaLogo}
                    alt="তন্তিকা"
                    className="h-16 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h1 className="text-2xl font-display font-bold bg-gradient-to-r from-clay-600 to-saffron-600 bg-clip-text text-transparent">তন্তিকা</h1>
                    <p className="text-sm text-cocoa-500">Handcrafted with ❤️</p>
                    <p className="text-sm text-cocoa-500">https://tantikacrafts.netlify.app</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-display font-bold tracking-widest text-cocoa-800">PACKING SLIP</div>
                  <div className="text-xs text-cocoa-400 mt-1">Date: {formatDate(new Date())}</div>
                  <div className="text-xs text-cocoa-400">Order: #{getOrderNumber()}</div>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <User className="w-4 h-4 text-clay-600" />
                    Customer Details
                  </h4>
                  <div className={infoBox}>
                    <p className="font-semibold text-cocoa-900">{getCustomerName()}</p>
                    <p className="text-cocoa-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {getCustomerPhone()}
                    </p>
                    <p className="text-cocoa-600 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {getCustomerEmail()}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <MapPin className="w-4 h-4 text-clay-600" />
                    Shipping Address
                  </h4>
                  <div className={infoBox}>
                    <p className="font-semibold text-cocoa-900">{getCustomerName()}</p>
                    <p className="text-cocoa-600 mt-1">{getCustomerAddress()}</p>
                    <p className="text-cocoa-600 mt-1">{getCustomerPhone()}</p>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <Calendar className="w-4 h-4 text-clay-600" />
                    Order Information
                  </h4>
                  <div className={infoBox}>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Order Date:</span> {formatDate(order.createdAt)}</p>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Artisan:</span> {getArtisanName()}</p>
                    <p className="text-cocoa-700"><span className="font-semibold text-cocoa-900">Payment:</span> {getPaymentMethod()}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className={sectionTitle}>
                    <CreditCard className="w-4 h-4 text-clay-600" />
                    Payment Summary
                  </h4>
                  <div className={infoBox}>
                    <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Subtotal:</span> {formatCurrency(subtotal)}</p>
                    <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Tax:</span> {formatCurrency(tax)}</p>
                    <p className="text-cocoa-700 flex justify-between"><span className="font-semibold text-cocoa-900">Shipping:</span> {formatCurrency(shippingCost)}</p>
                    <p className="font-bold text-clay-700 border-t border-clay-200 pt-1 flex justify-between">
                      <span>Total</span>
                      <span>{formatCurrency(total)}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Items Table - Only artisan's items */}
              <div className="space-y-2">
                <h4 className={sectionTitle}>
                  <Package className="w-4 h-4 text-clay-600" />
                  Items
                  <span className="text-xs text-cocoa-400 font-normal">(Your products only)</span>
                </h4>
                <div className="border border-cream-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-cream-50">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">#</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-cocoa-600">Item</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-cocoa-600">Qty</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Price</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-cocoa-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index} className="border-t border-cream-100">
                          <td className="px-3 py-2.5 text-cocoa-400">{index + 1}</td>
                          <td className="px-3 py-2.5">
                            <div>
                              <div className="font-medium text-cocoa-900">{item.name}</div>
                              {item.sku && <div className="text-xs text-cocoa-400">SKU: {item.sku}</div>}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center text-cocoa-800">{item.quantity}</td>
                          <td className="px-3 py-2.5 text-right text-cocoa-800">{formatCurrency(item.price)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-cocoa-900">
                            {formatCurrency((item.price || 0) * (item.quantity || 1))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-cream-50 border-t border-cream-200">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right font-semibold text-cocoa-700">Subtotal</td>
                        <td className="px-3 py-2 text-right font-semibold text-cocoa-900">{formatCurrency(subtotal)}</td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Shipping</td>
                        <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(shippingCost)}</td>
                      </tr>
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right text-sm text-cocoa-500">Tax</td>
                        <td className="px-3 py-2 text-right text-sm text-cocoa-700">{formatCurrency(tax)}</td>
                      </tr>
                      <tr className="border-t-2 border-clay-400">
                        <td colSpan="4" className="px-3 py-2.5 text-right font-bold text-clay-700">Total</td>
                        <td className="px-3 py-2.5 text-right font-bold text-clay-700">{formatCurrency(total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-between items-center border-t-2 border-cream-200 pt-4">
                <div className="text-xs text-cocoa-400">
                  <p>Thank you for shopping with তন্তিকা!</p>
                  <p className="mt-1">Scan the QR code to visit our website</p>
                </div>
                <div className="flex items-center gap-4">
                  {loading ? (
                    <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center">
                      <Loader className="w-6 h-6 animate-spin text-clay-600" />
                    </div>
                  ) : qrError ? (
                    <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center flex-col">
                      <AlertCircle className="w-6 h-6 text-red-500" />
                      <span className="text-xs text-red-500">QR Error</span>
                    </div>
                  ) : qrCodeUrl ? (
                    <img
                      src={qrCodeUrl}
                      alt="Visit তন্তিকা Website"
                      className="w-20 h-20 border-2 border-cream-200 rounded-xl"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-cream-100 rounded-xl flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-cocoa-300" />
                    </div>
                  )}
                  <div className="text-xs text-cocoa-500">
                    <p className="font-semibold text-cocoa-800">Scan to Visit Us!</p>
                    <p className="text-cocoa-400">তন্তিকা Crafts</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-cocoa-400 border-t border-cream-200 pt-4">
                <p>Generated on {formatDate(new Date())}</p>
                <p className="mt-1">This is a system generated packing slip for order #{getOrderNumber()}</p>
                <p className="mt-1">Thank you for supporting handcrafted products! 🙏</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN EXPORT - Smart component that selects the right version ==========
const OrderPackingSlip = ({ order, onClose }) => {
  // Determine user role and render appropriate version
  const getUserRole = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('tantika_user') || '{}');
      return userData.role || 'admin';
    } catch {
      return 'admin';
    }
  };

  const role = getUserRole();

  if (role === 'admin' || role === 'superAdmin') {
    return <AdminOrderPackingSlip order={order} onClose={onClose} />;
  } else if (role === 'artisan') {
    return <ArtisanOrderPackingSlip order={order} onClose={onClose} />;
  } else {
    // Default to admin version for unknown roles
    return <AdminOrderPackingSlip order={order} onClose={onClose} />;
  }
};

export default OrderPackingSlip;
